/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type {
  FetchInfiniteQueryOptions,
  FetchQueryOptions,
  InfiniteData,
  InitialDataFunction,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
} from '@tanstack/react-query';

type ApiError = {
  message?: string;
  details:
    | ({
        statusCode?: number;
        code: number;
        description?: string;
      } & Error)
    | null;
};

export const QueryNameSpace = {
  getNews: 'getNews',
  getNewsCategories: 'getNewsCategories',
  getNewsDetail: 'getNewsDetail',
  getNewsFeeds: 'getNewsFeeds',
  getNewsSources: 'getNewsSources',
  getReportCategories: 'getReportCategories',
  getReportCategoryDetail: 'getReportCategoryDetail',
  getReportCategoryUsers: 'getReportCategoryUsers',
  adminGetIssuePossibleDuplicates: 'adminGetIssuePossibleDuplicates',
  adminGetReportIssues: 'adminGetReportIssues',
  adminGetReportDuplicates: 'adminGetReportDuplicates',
  adminGetIssueStatements: 'adminGetIssueStatements',
  getReports: 'getReports',
  searchReports: 'searchReports',
  searchUsers: 'searchUsers',
  getUserReportCategories: 'getUserReportCategories',
  meGetNewsCategories: 'meGetNewsCategories',
  meGetNotifications: 'meGetNotifications',
  meGetNotificationsSettings: 'meGetNotificationsSettings',
  meGetNotificationsUnreadCount: 'meGetNotificationsUnreadCount',
  meGetReportCategories: 'meGetReportCategories',
  getReportDetail: 'getReportDetail',
  meGetReports: 'meGetReports',
  getReportStatus: 'getReportStatus',
  meNotificationsEmailStatus: 'meNotificationsEmailStatus',
  meNotificationsWebpushStatus: 'meNotificationsWebpushStatus',
  getProjects: 'getProjects',
  getProjectDetail: 'getProjectDetail',
  getVotings: 'getVotings',
  getVotingDetail: 'getVotingDetail',
  getVotingQuestions: 'getVotingQuestions',
  meGetGamificationAccounts: 'meGetGamificationAccounts',
  meGetGamificationTier: 'meGetGamificationTier',
  meGetGamificationAccountTransactions: 'meGetGamificationAccountTransactions',
  meGetGamificationTransaction: 'meGetGamificationTransaction',
  getVotingResults: 'getVotingResults',
  adminGetProjects: 'adminGetProjects',
  adminGetProjectDetail: 'adminGetProjectDetail',
  adminGetReportDetail: 'adminGetReportDetail',
  adminGetReports: 'adminGetReports',
  adminGetReportsAssignees: 'adminGetReportsAssignees',
  adminGetAiDecisions: 'adminGetAiDecisions',
  adminGetAiDecisionsStats: 'adminGetAiDecisionsStats',
  adminGetAiRuns: 'adminGetAiRuns',
  adminGetVotings: 'adminGetVotings',
  adminGetBenefits: 'adminGetBenefits',
  adminGetBenefitDetail: 'adminGetBenefitDetail',
  adminGetVotingDetail: 'adminGetVotingDetail',
  publicGetClientMessages: 'publicGetClientMessages',
  getMeMessages: 'getMeMessages',
  publicGetGamificationBenefits: 'publicGetGamificationBenefits',
  getFeed: 'getFeed',
  getCityBoundary: 'getCityBoundary',
  getUserNewsPreferences: 'getUserNewsPreferences',
  meGetTargetVoteTokens: 'meGetTargetVoteTokens',
  meGetVoteTokensSummary: 'meGetVoteTokensSummary',
  meGetVoteTokens: 'meGetVoteTokens',
  getUserConsentStatus: 'getUserConsentStatus',
} as const;

type QueryNameSpace = typeof QueryNameSpace;

export type QueryNameSpaceValue = QueryNameSpace[keyof QueryNameSpace];

/**
 * Oproti standardnej query funkcii tato obsahuje este funkciu `toString()`, ktora vrati
 * prvy prvok quey cache kluca. Standardne niektory z `QueryNameSpace` atributov.
 */
type QueryFunctionWithCacheKey<
  T = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = never,
> = QueryFunction<T, TQueryKey, TPageParam> & { toString: () => string };

/**
 * Oproti standardnej query funkcii tato obsahuje este funkciu `toString()`, ktora vrati
 * prvy prvok quey cache kluca. Standardne niektory z `QueryNameSpace` atributov.
 */
type InfiniteQueryFunctionWithCacheKey<
  T = unknown,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = never,
> = FetchInfiniteQueryOptions<
  T,
  TError,
  T,
  TQueryKey,
  TPageParam
>['queryFn'] & { toString: () => string };

/**
 * Query key je bud samostatny string alebo spolu so serializovanymi vstupnymi parametrami
 * do query funkcie.
 */
export type QueryKeySportnet<TParams = never> = [string] | [string, TParams];

/**
 * Query funkcii pridavame atribut `toString`, co je funkcia, ktora vrati kluc pre cache.
 */
export function createQueryFn<
  TParams = never,
  TQueryFnData = unknown,
  TPageParam = any,
>(
  key: QueryNameSpaceValue,
  queryFn: (
    context: QueryFunctionContext<[string] | [string, TParams], TPageParam>,
  ) => TQueryFnData | Promise<TQueryFnData>,
) {
  queryFn.toString = () => key;
  return queryFn;
}

export function fetchQ<
  TError = ApiError,
  TParams = never,
  TPageParam = any,
  TQueryFnData = unknown,
>(
  queryClient: QueryClient,
  queryFn: QueryFunctionWithCacheKey<
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    FetchQueryOptions<
      TQueryFnData,
      TError,
      TQueryFnData,
      QueryKeySportnet<TParams>,
      TPageParam
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return queryClient.fetchQuery<
    TQueryFnData,
    TError,
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >({
    queryKey: params ? [queryFn.toString(), params] : [queryFn.toString()],
    queryFn,
    ...options,
  });
}

export function prefetchQ<
  TError = ApiError,
  TParams = never,
  TPageParam = any,
  TQueryFnData = unknown,
>(
  queryClient: QueryClient,
  queryFn: QueryFunctionWithCacheKey<
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    FetchQueryOptions<
      TQueryFnData,
      TError,
      TQueryFnData,
      QueryKeySportnet<TParams>,
      TPageParam
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return queryClient.prefetchQuery<
    TQueryFnData,
    TError,
    TQueryFnData,
    QueryKeySportnet<TParams>
  >({
    queryKey: params ? [queryFn.toString(), params] : [queryFn.toString()],
    queryFn,
    ...options,
  });
}

export function prefetchInfiniteQ<
  TError = ApiError,
  TParams = never,
  TPageParam = any,
  TQueryFnData = unknown,
>(
  queryClient: QueryClient,
  queryFn: InfiniteQueryFunctionWithCacheKey<
    TQueryFnData,
    TError,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    FetchQueryOptions<
      TQueryFnData,
      TError,
      TQueryFnData,
      QueryKeySportnet<TParams>,
      TPageParam
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return queryClient.prefetchInfiniteQuery<
    TQueryFnData,
    TError,
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >({
    queryKey: params ? [queryFn.toString(), params] : [queryFn.toString()],
    queryFn,
    ...(options as any), // toto nie je OK pretoze prefetchInfiniteQuery ma typ TPageParam podla vzoru prefetchQuery by nemal byt
  });
}

export function fetchInfiniteQ<
  TError = ApiError,
  TParams = never,
  TPageParam = any,
  TQueryFnData = unknown,
>(
  queryClient: QueryClient,
  queryFn: InfiniteQueryFunctionWithCacheKey<
    TQueryFnData,
    TError,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    FetchQueryOptions<
      TQueryFnData,
      TError,
      TQueryFnData,
      QueryKeySportnet<TParams>,
      TPageParam
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return queryClient.fetchInfiniteQuery<
    TQueryFnData,
    TError,
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >({
    queryKey: params ? [queryFn.toString(), params] : [queryFn.toString()],
    queryFn,
    ...(options as any), // toto nie je OK pretoze prefetchInfiniteQuery ma typ TPageParam podla vzoru prefetchQuery by nemal byt
  });
}

export function useQ<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TError = ApiError,
  TParams = never,
  TPageParam = any,
>(
  queryFn: QueryFunctionWithCacheKey<
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, QueryKeySportnet<TParams>>,
    'queryKey' | 'queryFn' | 'select' | 'initialData'
  > & {
    select?: (data: TQueryFnData) => TData;
    initialData?: TQueryFnData | InitialDataFunction<TQueryFnData>;
  },
) {
  const queryKey = (
    params ? [queryFn.toString(), params] : [queryFn.toString()]
  ) as QueryKeySportnet<TParams>;

  const queryRes = useQuery<
    TQueryFnData,
    TError,
    TData,
    QueryKeySportnet<TParams>
  >({
    queryKey,
    queryFn,
    ...options,
  });

  return {
    ...queryRes,
    queryKey,
  };
}

export function useInfiniteQ<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TError = ApiError,
  TParams = never,
  TPageParam = any,
>(
  queryFn: InfiniteQueryFunctionWithCacheKey<
    TQueryFnData,
    TError,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TQueryFnData,
      QueryKeySportnet<TParams>,
      TPageParam
    >,
    'queryKey' | 'queryFn' | 'select' | 'initialData'
  > & {
    select?: (data: TQueryFnData) => TData;
    initialData?: TQueryFnData | InitialDataFunction<TQueryFnData>;
  },
) {
  const queryKey = (
    params ? [queryFn.toString(), params] : [queryFn.toString()]
  ) as QueryKeySportnet<TParams>;

  const queryRes = useInfiniteQuery<
    TQueryFnData,
    TError,
    InfiniteData<TData>,
    QueryKeySportnet<TParams>
  >({
    queryKey,
    queryFn: queryFn as any,
    ...(options as any),
  });

  return {
    ...queryRes,
    queryKey,
  };
}

export function useSuspenseQ<
  TQueryFnData = unknown,
  TData = TQueryFnData,
  TError = ApiError,
  TParams = never,
  TPageParam = any,
>(
  queryFn: QueryFunctionWithCacheKey<
    TQueryFnData,
    QueryKeySportnet<TParams>,
    TPageParam
  >,
  params?: TParams,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, QueryKeySportnet<TParams>>,
    'queryKey' | 'queryFn' | 'select' | 'initialData'
  > & {
    select?: (data: TQueryFnData) => TData;
    initialData?: TQueryFnData | InitialDataFunction<TQueryFnData>;
  },
) {
  return useSuspenseQuery<
    TQueryFnData,
    TError,
    TData,
    QueryKeySportnet<TParams>
  >({
    queryKey: params ? [queryFn.toString(), params] : [queryFn.toString()],
    queryFn,
    ...options,
  });
}

export function useM<
  TData = unknown,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationFn'
  >,
): UseMutationResult<TData, TError, TVariables, TContext> {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn,
    ...options,
  });
}
